import { Suspense, useEffect, useMemo, useRef } from "react";
import { useVideoTexture } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { Mesh, PerspectiveCamera } from "three";

type VideoAnimationProps = {
  videoSrc: string;
  visible: boolean;
  muted?: boolean;
  onAnimationComplete: () => void;
};

const VideoAnimation: React.FC<VideoAnimationProps> = ({
  videoSrc,
  visible,
  muted,
  onAnimationComplete,
}) => {
  const planeRef = useRef<Mesh | null>(null);
  const camera = useThree((props) => props.camera);
  const size = useThree((props) => props.size);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (planeRef.current && camera) {
      camera.add(planeRef.current);
    }
  }, [planeRef, camera]);

  // Create the video element only if the video src changesr
  const videoTexture = useVideoTexture(videoSrc, {
    loop: false,
    start: false,
  });

  // Since useVideoTexture memo's the props we need to manually update the muted prop in case the consuming library updates it in flight
  useEffect(() => {
    if (videoTexture.image) {
      (videoTexture.image as HTMLVideoElement).muted = muted ?? true;
    }
  }, [muted, videoTexture.image]);

  // Register on ended callback
  (videoTexture.image as HTMLVideoElement).onended = () => {
    onAnimationComplete();
  };

  // Whenever visibility changes it means we have to trigger the video play
  useEffect(() => {
    const videoPlay = async () => {
      if (visible) await (videoTexture.image as HTMLVideoElement).play();
    };
    videoPlay().catch((e) => {
      //@Todo do we want to catch this?
    });

    return () => {
      if (visible) {
        (videoTexture.image as HTMLVideoElement).pause();
        (videoTexture.image as HTMLVideoElement).currentTime = 0;
      }
    };
  }, [visible, videoTexture.image]);

  const planeDepth = -camera.near;

  const planeHeight = useMemo(() => {
    return visibleHeightAtZDepth(planeDepth, camera as PerspectiveCamera);
  }, [camera, size]);

  const videoElement = videoTexture.source.data as HTMLVideoElement;
  const isLandscapeVideo = videoElement.videoWidth >= videoElement.videoHeight;
  const videoAspect = useMemo(() => {
    return isLandscapeVideo
      ? videoElement.videoWidth / videoElement.videoHeight
      : videoElement.videoHeight / videoElement.videoWidth;
  }, [videoTexture, size, videoSrc]);

  return (
    <Suspense>
      <mesh ref={planeRef} visible={visible} position-z={planeDepth}>
        <planeGeometry
          args={
            isLandscapeVideo
              ? [planeHeight * videoAspect, planeHeight]
              : [planeHeight, planeHeight * videoAspect]
          }
        />
        <meshBasicMaterial map={videoTexture} />
      </mesh>
    </Suspense>
  );
};

const visibleHeightAtZDepth = (depth: number, camera: PerspectiveCamera) => {
  // vertical fov in radians
  const vFOV = (camera.fov * Math.PI) / 180;

  // Math.abs to ensure the result is always positive
  return 2 * Math.tan(vFOV / 2) * Math.abs(depth);
};

export default VideoAnimation;
